import { Box, Button, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import ExportButton from "../../../../components/buttons/ExportButton";
import SearchInput from "../../../../components/filters/SearchInput";
import { Toast } from "../../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import { useInventoryBootstrap } from "../../../../contexts/InventoryBootstrapContext";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import { QueryParam } from "../../../../utils/constants";
import ColumnSettingsModal, {
  getVisibleColumnsForEntity,
} from "../components/ColumnSettingsModal";
import { RDS_ENTITIES_QUERY_KEY_PREFIX } from "../components/constants";
import EditLabelsButton from "../components/EditLabelsButton";
import EditTagsButton from "../components/EditTagsButton";
import EntityChartView from "../components/entitiesCharts/EntityChartView";
import EntityDrawer from "../components/EntityDrawer/EntityDrawer";
import MarkAsCriticalButton from "../components/MarkAsCriticalButton/MarkAsCriticalButton";
import SearchBox from "../components/SearchBox";
import EntitiesTable from "../entitiesTable/EntitiesTable";
import RdsEntitiesTable from "../entitiesTable/RdsEntitiesTable";
import FiltersBox from "../filters/FiltersBox";
import useFilterEntities from "../filters/useFilterEntities";
import {
  filterName,
  getInitialFilters,
  getInitialLogics,
} from "../filters/utils";
import useEntitiesExport from "../hooks/useEntitiesExport";
import useRdsEntitiesInfinite from "../hooks/useRdsEntitiesInfinite";
import useRdsEntityById from "../hooks/useRdsEntityById";
import EntityTypeBackButton from "./EntityTypeBackButton";
import SmallEntityTypeCards from "./SmallEntityTypeCards";

export default function EntityTypeView({
  entityType,
  fetchedEntities: legacyFetchedEntities,
  entitiesAreFetching = false,
  entitiesOverviewData,
  assets,
}) {
  const colors = useColors();
  const gridRef = useGridApiRef();
  const { trackEvent } = useAPI();
  const { exportEntities } = useEntitiesExport();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);
  const { generateFileName } = useFileNaming();
  const { hasMultipleSelectedTenants } = useMSSP();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getInitialFilters(entityType)
  );
  const [filterAndOrLogics, setFilterAndOrLogics] = useState(() =>
    getInitialLogics(entityType)
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    local_user_admin: false,
  });
  const [sortModel, setSortModel] = useState([
    { field: "risk", sort: "desc" },
    { field: "display_name", sort: "asc" },
  ]);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const { labels: allLabels, tags: allCustomTags } = useInventoryBootstrap();

  const onDrawerClose = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete(QueryParam.entity);
    setSearchParams(updatedSearchParams);
    trackEvent("User closed the entity drawer");
  };

  const handleTableExport = async () => {
    trackEvent(
      "User click on 'Export' button to download the environment table"
    );
    if (isActiveRds) {
      setIsExporting(true);
      try {
        await exportEntities({
          entity_type: entityType,
          columns: visibleColumns,
          sort_by: sortModel?.[0]?.field || null,
          sort_dir: sortModel?.[0]?.sort || null,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to export entities. Please try again.";
        Toast.error(errorMessage);
        trackEvent("Failed to export entities by type", {
          entity_type: entityType,
          error: errorMessage,
        });
      } finally {
        setIsExporting(false);
      }
      return;
    }
    const csvExportOptions = {
      fileName: generateFileName(`${pluralize(entityType)}`),
      allColumns: true,
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  const handleOpenColumnSettings = () => {
    trackEvent("User opened entity column settings");
    setColumnSettingsOpen(true);
  };

  const handleColumnsChange = (newSelectedColumns) => {
    setVisibleColumns(newSelectedColumns);
    trackEvent("User updated entity column visibility");
  };

  const handleSearch = (q) => {
    trackEvent("User searched entities by type", {
      entity_type: entityType,
      query_length: q.length,
    });
  };

  const handleClearSearch = () => {
    trackEvent("User cleared entity search by type", {
      entity_type: entityType,
    });
  };

  useEffect(() => {
    if (isActiveRds && entityType) {
      const savedColumns = getVisibleColumnsForEntity(entityType);
      setVisibleColumns(savedColumns);
    } else {
      setVisibleColumns(null);
    }
  }, [isActiveRds, entityType]);

  const filteredEntities = useFilterEntities(
    legacyFetchedEntities,
    appliedFilters,
    filterAndOrLogics,
    assets
  );

  const rdsQuery = useRdsEntitiesInfinite({
    entityType,
    sortModel,
    visibleColumns,
    filtersPayload: null,
    freetextQuery: activeSearch,
    enabled: isActiveRds,
    queryKeyPrefix: RDS_ENTITIES_QUERY_KEY_PREFIX,
  });
  const rdsRows = rdsQuery.data?.entities || [];
  const rdsTotalCount = rdsQuery.data?.totalCount || 0;
  const hasLoadedOnce = Boolean(rdsQuery.data);

  const viewedEntityUniqueId = decodeURIComponent(
    searchParams.get(QueryParam.entity)
  );
  // The entity that the url query param "entity" is pointing to (used for the entity drawer)
  const entityFromTable = viewedEntityUniqueId
    ? (isActiveRds ? rdsRows : legacyFetchedEntities)?.find(
        (row) => row.unique_id === viewedEntityUniqueId
      )
    : null;

  const {
    data: fetchedEntityRds,
    isLoading: entityDetailsIsLoading,
    isFetching: entityDetailsIsFetching,
  } = useRdsEntityById({
    entityId: entityFromTable?.entity_id,
    customerId: entityFromTable?.customer_id,
    enabled: Boolean(
      isActiveRds && entityFromTable?.entity_id && entityFromTable?.customer_id
    ),
  });

  const viewedEntity = fetchedEntityRds || entityFromTable;

  const checkedEntities = (isActiveRds ? rdsRows : filteredEntities).filter(
    (entity) =>
      checkedEntityIDs.includes(entity.entity_id + "-" + entity.customer_id)
  );

  const checkedEntitiesIds = checkedEntities.map((entity) => entity.entity_id);
  if (!entityType) return null;

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <EntityTypeBackButton />
        <SmallEntityTypeCards entitiesOverviewData={entitiesOverviewData} />
        {!isActiveRds && (
          <FiltersBox
            entityType={entityType}
            fetchedEntities={legacyFetchedEntities}
            assets={assets}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            filterAndOrLogics={filterAndOrLogics}
            setFilterAndOrLogics={setFilterAndOrLogics}
            allLabels={allLabels}
            allCustomTags={allCustomTags}
          />
        )}
        <Box display="flex" justifyContent="space-between">
          {!isActiveRds ? (
            <Box display="flex" gap={1} width="350px">
              <Box flex={1}>
                <SearchInput
                  fieldName={filterName.SEARCH_STRING}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  minSearchLength={3}
                  placeholder="Search entities..."
                />
              </Box>
            </Box>
          ) : (
            <SearchBox
              colors={colors}
              placeholder={`Search ${pluralize(entityType)}...`}
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              activeSearch={activeSearch}
              onActiveSearchChange={setActiveSearch}
              minSearchLength={3}
              onSearch={handleSearch}
              onClear={handleClearSearch}
              rightContent={
                hasLoadedOnce ? (
                  <Typography variant="body2" color={colors.text_secondary}>
                    Showing {rdsRows.length.toLocaleString()} of{" "}
                    {rdsTotalCount.toLocaleString()} entities
                  </Typography>
                ) : (
                  <Typography variant="body2" color={colors.text_secondary}>
                    Loading entities…
                  </Typography>
                )
              }
            />
          )}
          <Box display="flex" gap={1}>
            {!hasMultipleSelectedTenants && (
              <>
                <EditLabelsButton checkedEntities={checkedEntities} />
                <EditTagsButton checkedEntities={checkedEntities} />
                <MarkAsCriticalButton
                  checkedEntityIDs={checkedEntitiesIds}
                  assets={assets}
                />
              </>
            )}
            {isActiveRds && (
              <Button
                onClick={handleOpenColumnSettings}
                sx={{
                  color: colors.text_primary,
                  height: "30px",
                  border: "1px solid " + colors.grey_48percent,
                  textTransform: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  background: "white",
                }}
              >
                <Typography variant="overline">Columns</Typography>
              </Button>
            )}
            <ExportButton
              handleExport={handleTableExport}
              sx={{
                border: "1px solid " + colors.grey_48percent,
                background: "white",
              }}
              disabled={isExporting}
            />
          </Box>
        </Box>
        <Box>
          {isActiveRds ? (
            <RdsEntitiesTable
              entityType={entityType}
              rows={rdsRows}
              assets={assets}
              visibleColumns={visibleColumns}
              sortModel={sortModel}
              onSortModelChange={(model) => setSortModel(model)}
              onLoadMore={() => rdsQuery.fetchNextPage()}
              hasMore={rdsQuery.hasNextPage}
              isLoading={rdsQuery.isLoading}
              isFetching={rdsQuery.isFetching}
              isLoadingMore={rdsQuery.isFetchingNextPage}
              totalCount={rdsTotalCount}
              setCheckedEntityIDs={setCheckedEntityIDs}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
            />
          ) : (
            <EntitiesTable
              entityType={entityType}
              entitiesAreLoading={false}
              entitiesAreFetching={entitiesAreFetching}
              filteredEntities={filteredEntities}
              setCheckedEntityIDs={setCheckedEntityIDs}
              fetchedEntities={legacyFetchedEntities}
              assets={assets}
              gridRef={gridRef}
              visibleColumns={visibleColumns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
            />
          )}
        </Box>
        <EntityChartView
          entityType={entityType}
          entities={isActiveRds ? rdsRows : filteredEntities}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
        />
      </Box>

      <EntityDrawer
        openDrawer={viewedEntity}
        selectedEntity={viewedEntity}
        entityDetailsIsLoading={
          Boolean(
            isActiveRds &&
              entityFromTable?.entity_id &&
              entityFromTable?.customer_id
          ) &&
          (entityDetailsIsLoading || entityDetailsIsFetching)
        }
        onClose={onDrawerClose}
      />

      {isActiveRds && (
        <ColumnSettingsModal
          entities={filteredEntities}
          open={columnSettingsOpen}
          onClose={() => setColumnSettingsOpen(false)}
          entityType={entityType}
          onColumnsChange={handleColumnsChange}
        />
      )}
    </>
  );
}
